/* organization/OrganizationLayout — Organization-scoped console shell.
   Same nx-cc-* design language as the Platform tier but scoped to a single
   organization (resolved from :orgId). Distinct topbar identity ("Org Console")
   so admins know which tier they're in. Clean ESM. */
import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { NavLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill } from '../shared/ui';
import UserMenu from '../shared/auth/UserMenu.jsx';
import { PlatformFooter } from '../platform/PlatformLayout';
import { statusTone } from '../platform/platformStore';
import { hasAnyFeature, computeEffectiveFeatures } from '../platform/featureRegistry.js';
import { useOrgNavigation, useOrgRuntime, useOrganization } from './OrganizationRuntimeContext.jsx';
import { applyBrandingTokens, clearBrandingTokens, getLastWorkspaceBranding } from '../shared/branding/index.js';
import OrgBreadcrumb from './OrgBreadcrumb.jsx';

/* FOUNDATION-004B: features arrays use FOUNDATION-003 canonical leaf keys.
   Parent-key resolution in hasAnyFeature() ensures pre-v2.0 DB rows (which stored
   parent shorthands like 'org.teams') still grant access without a migration. */
const navItems = (orgId) => [
  { group: '', items: [
    { to: `/organization/${orgId}`,              end: true, label: 'Dashboard',          icon: 'LayoutDashboard' },
  ]},
  { group: 'Administration', items: [
    { to: `/organization/${orgId}/members`,                label: 'Members',             icon: 'Users'         },
    { to: `/organization/${orgId}/teams`,                  label: 'Teams',               icon: 'UsersRound',   features: ['org.teams.view'] },
    { to: `/organization/${orgId}/iam`,                    label: 'Identity & Access',   icon: 'Lock'          },
    { to: `/organization/${orgId}/workspaces`,             label: 'Workspaces',          icon: 'LayoutGrid'    },
    { to: `/organization/${orgId}/licenses`,               label: 'Licenses',            icon: 'Key'           },
    { to: `/organization/${orgId}/billing`,                label: 'Billing',             icon: 'CreditCard'    },
  ]},
  { group: 'Configuration', items: [
    { to: `/organization/${orgId}/branding`,               label: 'Branding',            icon: 'Eye',          features: ['org.branding.view', 'org.whitelabel.configure'] },
    { to: `/organization/${orgId}/ai`,                     label: 'AI Platform',         icon: 'Cpu',          features: ['ai.copilot.use', 'ai.agents.configure', 'ai.knowledge-base.create', 'ai.email-assist.use', 'ai.prompts.create', 'ai.models.configure'] },
    { to: `/organization/${orgId}/channels`,               label: 'Channels',            icon: 'MessageSquare', features: ['channels.email.use', 'channels.whatsapp.use', 'channels.sms.use', 'channels.voice.use', 'channels.webchat.use', 'channels.social.facebook.use', 'channels.social.instagram.use', 'channels.social.telegram.use', 'channels.slack.use'] },
    { to: `/organization/${orgId}/storage`,                label: 'Storage',             icon: 'HardDrive',    features: ['storage.documents'] },
    { to: `/organization/${orgId}/notifications`,          label: 'Notifications',       icon: 'Bell',         features: ['org.notifications.view'] },
    { to: `/organization/${orgId}/developer`,              label: 'Developer Center',    icon: 'Code',         features: ['developer.api-keys.view', 'developer.webhooks.view', 'developer.oauth-apps.view', 'developer.sdk.access'] },
    { to: `/organization/${orgId}/integrations`,           label: 'Integrations',        icon: 'Plug',         features: ['org.integrations.view'] },
    { to: `/organization/${orgId}/support`,                label: 'Support Center',      icon: 'LifeBuoy'      },
  ]},
  { group: 'Security', items: [
    { to: `/organization/${orgId}/audit`,                  label: 'Audit Logs',          icon: 'ClipboardList', features: ['org.audit.view'] },
    { to: `/organization/${orgId}/timeline`,               label: 'Activity Timeline',   icon: 'Activity'      },
    { to: `/organization/${orgId}/security`,               label: 'Security Center',     icon: 'ShieldCheck',  features: ['org.security.view'] },
  ]},
  { group: 'Enterprise', items: [
    { to: `/organization/${orgId}/analytics`,              label: 'Analytics',           icon: 'BarChart2',    features: ['analytics.crm.basic', 'analytics.crm.advanced', 'analytics.crm.revenue', 'analytics.org.users'] },
    { to: `/organization/${orgId}/insights`,               label: 'Insights',            icon: 'Lightbulb',    features: ['analytics.crm.basic', 'analytics.crm.advanced', 'analytics.crm.revenue', 'analytics.org.users'] },
    { to: `/organization/${orgId}/usage`,                  label: 'Usage & Quotas',      icon: 'Gauge'         },
    { to: `/organization/${orgId}/health`,                 label: 'Health Center',       icon: 'HeartPulse'    },
    { to: `/organization/${orgId}/search`,                 label: 'Search',              icon: 'Search'        },
  ]},
  { group: 'System', items: [
    { to: `/organization/${orgId}/backup`,                 label: 'Backup & Restore',    icon: 'Archive',      features: ['org.backup.view'] },
    { to: `/organization/${orgId}/settings`,               label: 'Settings',            icon: 'Settings'      },
  ]},
];

function groupServerNav(serverItems) {
  return Object.values(
    serverItems
      .filter(it => it.visible)
      .reduce((acc, it) => {
        const g = it.group ?? '';
        if (!acc[g]) acc[g] = { group: g, items: [] };
        acc[g].items.push({ to: it.path, end: it.end, label: it.label, icon: it.icon });
        return acc;
      }, {}),
  );
}

function buildStaticNav(orgId, org, isSA) {
  const effectiveFeatures = computeEffectiveFeatures(org);
  return navItems(orgId)
    .map(grp => ({ ...grp, items: grp.items.filter(it => isSA || !it.features || hasAnyFeature(effectiveFeatures, it.features)) }))
    .filter(grp => grp.items.length > 0);
}

export function resolveExitNav(isSA, cameFromCrm, activeWsId) {
  if (isSA) return { path: '/platform', label: 'Control Center' };
  if (cameFromCrm) {
    return { path: activeWsId ? '/app/' + activeWsId : '/', label: 'Back to CRM' };
  }
  return { path: '/workspaces', label: 'Workspaces' };
}

export default function OrganizationLayout({ data, children }) {
  const { orgId } = useParams();
  const { signOut, user } = window.useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSA = user?.platformRole === 'SUPER_ADMIN';

  /* Runtime context — server-computed nav, branding, and org (Phase 0).
     runtimeOrg is the authoritative source for the current org object.
     legacyOrg is a fallback for SA browsing orgs before their runtime loads. */
  const { runtime, loading: runtimeLoading } = useOrgRuntime();
  const runtimeOrg      = useOrganization();
  const runtimeNav      = useOrgNavigation();
  const runtimeBranding = runtime?.branding ?? null;

  const legacyOrg   = data.state.organizations.find(o => o.id === orgId) ?? null;
  const org         = runtimeOrg ?? legacyOrg;
  /* Use runtimeBranding only — the legacy data.state.branding?.[orgId] from platformStore
     has a mismatched key format (primary vs primaryColor, no accentColor) that causes
     applyBrandingTokens to compute --nx-accent = primary = '#125AB0' (BLUE fallback). */
  const orgBranding = runtimeBranding;
  const isLoading   = runtimeLoading || Boolean(data.state.loading);

  /* Mount guard: clear workspace CSS vars synchronously before the first paint.
     When the user navigates from the CRM (which applies workspace white-label
     branding to :root inline styles via applyBrandingGlobally), those vars
     persist until the org runtime fetch completes — orgBranding is null on the
     first render because the 5-minute runtime cache is cold on initial org visit.
     Without this clear the shell shows the workspace's brand color (potentially
     green) for the entire async window. Clearing on mount makes the shell fall
     back to the stylesheet default (#125AB0) immediately, before any network
     round-trip. Must be declared BEFORE the [orgBranding] effect so mount order
     is: clear-first, then (if cache is warm) apply-org-branding. */
  useLayoutEffect(() => {
    clearBrandingTokens();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* CSS variables applied synchronously before paint — eliminates the blue flash
     on first render and ensures Apply Branding takes effect in the same frame
     as the React state update, with no refresh required.
     Delegates to the canonical applyBrandingTokens so all 13 branding tokens
     (accent-hover, accent-active, accent-tint-2, *-tint variants, font) are
     always written together — no partial updates, no drift from the shared module. */
  useLayoutEffect(() => {
    if (!orgBranding) return;
    applyBrandingTokens(orgBranding);
    /* On Org Console exit: clear org-specific CSS vars then immediately restore
       the last workspace branding (if any) so CRM and Workspace Console surfaces
       see their correct branding as soon as they mount — before the browser paints.
       Without this, a concurrent-mode commit split can produce a visible BLUE flash
       between OrgLayout clearing and WorkspaceShell/WorkspaceConsoleLayout restoring. */
    return () => {
      clearBrandingTokens();
      const ws = getLastWorkspaceBranding();
      if (ws) applyBrandingTokens(ws);
    };
  }, [orgBranding]);

  /* Non-visual side effects: title, font, favicon (can be async after paint) */
  useEffect(() => {
    if (org?.name) document.title = (orgBranding?.logoText || org.name) + ' — Org Console';
    if (!orgBranding) return;
    const root = document.documentElement;

    const font = orgBranding.font;
    if (font) {
      root.style.setProperty('--nx-font', `"${font}", ui-sans-serif, system-ui, -apple-system, sans-serif`);
      let fontLink = document.getElementById('nx-brand-font');
      if (!fontLink) {
        fontLink = document.createElement('link');
        fontLink.id  = 'nx-brand-font';
        fontLink.rel = 'stylesheet';
        document.head.appendChild(fontLink);
      }
      fontLink.href = `https://fonts.googleapis.com/css2?family=${font.replaceAll(' ', '+')}:wght@400;500;600;700;800&display=swap`;
    }

    if (orgBranding.faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = orgBranding.faviconUrl;
    }
  }, [orgBranding, org?.name]);

  const handleLogout = useCallback(async () => {
    await signOut().catch(() => {});
    navigate('/', { replace: true });
  }, [signOut, navigate]);

  /* Context-aware exit.
     SA always → Control Center.
     OA: only show "Back to CRM" if the user explicitly navigated here from the CRM
     (state.from === 'crm' is set by ProfileMenu and WorkspaceSwitcher when leaving CRM).
     Otherwise send them to the Workspace Directory — never guess which CRM to open. */
  const cameFromCrm = location.state?.from === 'crm';
  const activeWsId  = user?.activeWorkspaceId ?? null;
  const { path: exitPath, label: exitLabel } = resolveExitNav(isSA, cameFromCrm, activeWsId);

  /* Dynamic role in UserMenu pill */
  const roleLabel = isSA ? 'Super Admin' : 'Org Admin';
  const roleTone  = isSA ? 'purple' : 'blue';

  /* RG-002: explicit scope guard — non-SA users must not access orgs outside their membership.
     Wait for runtime to finish loading before redirecting so a direct-navigation Org Admin
     isn't bounced before runtimeOrg has had a chance to resolve. */
  if (!isSA && !runtimeLoading && data.state.organizations.length > 0 && !org) {
    navigate('/workspaces', { replace: true });
    return null;
  }

  if (!org) {
    return (
      <div className="nx-cc-root nx-org-root">
        <div className="nx-cc-topbar">
          <button className="nx-cc-exit-btn" onClick={() => navigate(exitPath)}><NexusIcon name="ChevronLeft" size={14} /><span>{exitLabel}</span></button>
          <div className="nx-cc-topbar-brand"><span className="nx-cc-topbar-title">{isLoading ? 'Loading…' : 'Organization not found'}</span></div>
        </div>
        {isLoading ? (
          <div className="nx-cc-workspace">
            <div className="nx-cc-sidenav">
              {['s1','s2','s3','s4','s5','s6','s7'].map(k => (
                <div key={k} style={{ height: 32, margin: '2px 8px', borderRadius: 6, background: 'var(--nx-surface-2, #222938)', opacity: 0.3 }} />
              ))}
            </div>
            <div className="nx-cc-content" />
          </div>
        ) : (
          <div className="nx-cc-content"><div className="nx-empty" style={{ marginTop: 60 }}><NexusIcon name="Building2" size={24} /><span>This organization does not exist.</span></div></div>
        )}
      </div>
    );
  }

  /* Phase 0: use server-computed nav when available.
     During initial runtime load (runtimeLoading + no server nav yet), keep NAV empty
     rather than showing the broken static fallback whose computeEffectiveFeatures() always
     returns [] for non-SA users (planFeatureKeys not populated from /orgs endpoint). */
  let NAV = [];
  if (runtimeNav.length > 0) {
    NAV = groupServerNav(runtimeNav);
  } else if (!runtimeLoading) {
    NAV = buildStaticNav(orgId, org, isSA);
  }

  return (
    <div className="nx-cc-root nx-org-root">
      <div className="nx-cc-topbar">
        <button className="nx-cc-exit-btn" onClick={() => navigate(exitPath)}>
          <NexusIcon name="ChevronLeft" size={14} />
          <span>{exitLabel}</span>
        </button>
        <button
          type="button"
          className="nx-cc-topbar-brand"
          style={{ cursor: 'pointer', background: 'none', border: 'none', font: 'inherit', padding: 0 }}
          onClick={() => navigate(`/organization/${orgId}`)}
        >
          {orgBranding?.logoUrl
            ? <img src={orgBranding.logoUrl} alt={org.name} className="nx-cc-topbar-org-logo" style={{ height: 22, objectFit: 'contain', borderRadius: 4 }} />
            : <img src="/ursa-logo.svg" alt="URSA" className="nx-cc-topbar-org-logo" style={{ height: 22, width: 22, objectFit: 'contain', borderRadius: 4 }} />}
          <span className="nx-cc-topbar-title">{orgBranding?.logoText || org.name}</span>
          <Pill tone="blue">Org Console</Pill>
          <Pill tone={statusTone(org.status)}>{org.status}</Pill>
        </button>
        <div className="nx-cc-topbar-right" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="nx-cc-topbar-meta">{org.plan} · {org.users} users</span>
          <UserMenu roleLabel={roleLabel} roleTone={roleTone} onLogout={handleLogout} onProfile={() => navigate(user?.platformRole === 'SUPER_ADMIN' ? '/platform/profile' : `/organization/${orgId}/profile`)} />
        </div>
      </div>
      <div className="nx-cc-workspace">
        <div className="nx-cc-sidenav">
          {NAV.map(grp => (
            <div key={grp.group || '_top'} className="nx-cc-nav-group">
              {grp.group && <span className="nx-cc-nav-group-label">{grp.group}</span>}
              {grp.items.map(it => (
                <NavLink key={it.to} to={it.to} end={it.end} title={it.label}
                  className={({ isActive }) => 'nx-cc-nav-item' + (isActive ? ' is-active' : '')}>
                  <NexusIcon name={it.icon} size={15} />
                  <span style={{ flex: 1 }}>{it.label}</span>
                  {it.soon && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'color-mix(in srgb, var(--nx-accent, #125AB0) 9%, transparent)', color: 'var(--nx-accent, #125AB0)', letterSpacing: '0.04em' }}>SOON</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
        <div className="nx-cc-content">
          <OrgBreadcrumb />
          {children}
        </div>
      </div>
      <PlatformFooter />
    </div>
  );
}
